"use client";

import { useEffect, useId, useState, useSyncExternalStore } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperInstance } from "swiper/types";
import ArticleLink from "@/components/article/article_link";
import SaveArticle from "@/components/reader/save_article";
import "swiper/css";
import styles from "./spotlight.module.css";

const AUTOPLAY_DELAY_MS = 5000;

export type SpotlightArticle = {
	id: string;
	title: string;
	url: string;
	image: string;
	fallbackImage: string;
	host: string;
	publishedAt: string;
	publishedLabel: string;
};

/** 動きを減らす設定を購読する。入力は通知関数、戻り値は解除関数。ブラウザー専用で設定変更を監視する。 */
function subscribeMotion(onChange: () => void): () => void {
	const media = window.matchMedia("(prefers-reduced-motion: reduce)");
	media.addEventListener("change", onChange);
	return () => media.removeEventListener("change", onChange);
}

/** 現在の動きを減らす設定を返す。引数・副作用・例外なし。ブラウザー専用。 */
function reducedMotionSnapshot(): boolean {
	return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** タブの表示変更を購読する。入力は通知関数、戻り値は解除関数。DOMイベントの購読以外の副作用なし。 */
function subscribeVisibility(onChange: () => void): () => void {
	document.addEventListener("visibilitychange", onChange);
	return () => document.removeEventListener("visibilitychange", onChange);
}

/** タブが非表示かを返す。引数・副作用・例外なし。ブラウザー専用。 */
function hiddenSnapshot(): boolean {
	return document.visibilityState !== "visible";
}

/** SSR時は自動切替を停止する。引数・副作用・例外なし。 */
function stoppedServerSnapshot(): boolean {
	return true;
}

/**
 * 公開記事の画像を表示し、読込エラー時だけ共通の代替画像へ切り替える。
 * 入力は画像URLと先頭画像フラグ、戻り値は画像要素。画像通信以外の外部副作用・例外なし。
 */
function SpotlightImage({
	source,
	fallback,
	first,
}: {
	source: string;
	fallback: string;
	first: boolean;
}) {
	const [failedSource, setFailedSource] = useState<"primary" | "fallback" | null>(null);
	const imageSource =
		failedSource === "fallback"
			? "/images/no-image/no-image.webp"
			: failedSource === "primary"
				? fallback || "/images/no-image/no-image.webp"
				: source || fallback || "/images/no-image/no-image.webp";
	return (
		<Image
			src={imageSource}
			alt=""
			fill
			priority={first}
			sizes="(max-width: 700px) 100vw, (max-width: 1200px) 60vw, 690px"
			className={styles.image}
			onError={() => setFailedSource((current) => (current === "primary" ? "fallback" : "primary"))}
		/>
	);
}

/**
 * 文字列だけの上位記事を受け取り、画像スライドと同じ順番の一覧を返す。
 * 自動切替は5秒間隔。非表示タブ・動作軽減設定では一時的に停止する。
 * キーボード操作では明示的に停止し、再開ボタンを押すまで再開しない。
 * 保存とリンク計測は共通コンポーネントへ委譲する。Swiperは巻き戻しを使い、スライドを複製しない。
 * 副作用はタイマー・設定の購読・利用者による保存/計測。DB操作なし。
 */
export default function SpotlightCarousel({
	articles,
	source,
}: {
	articles: SpotlightArticle[];
	source: "popular" | "latest";
}) {
	const carouselId = useId();
	const [swiper, setSwiper] = useState<SwiperInstance | null>(null);
	const [active, setActive] = useState(0);
	const [manuallyPaused, setManuallyPaused] = useState(false);
	const reducedMotion = useSyncExternalStore(
		subscribeMotion,
		reducedMotionSnapshot,
		stoppedServerSnapshot,
	);
	const hidden = useSyncExternalStore(subscribeVisibility, hiddenSnapshot, stoppedServerSnapshot);
	const multiple = articles.length > 1;
	const autoplay = multiple && !manuallyPaused && !reducedMotion && !hidden;

	/** 自動切替が有効な間、Swiperを5秒ごとに進める。解除時はタイマーを破棄し、破棄済みインスタンスは触らない。 */
	useEffect(() => {
		if (!swiper || swiper.destroyed || !autoplay) return;
		const timer = window.setInterval(() => {
			if (!swiper.destroyed) swiper.slideNext(380);
		}, AUTOPLAY_DELAY_MS);
		return () => window.clearInterval(timer);
	}, [autoplay, swiper]);

	/** 選択された記事番号へ移動する。入力は配列添字。存在しない添字は無視し、戻り値・外部通信なし。 */
	function selectSlide(index: number) {
		if (!swiper || swiper.destroyed || index < 0 || index >= articles.length) return;
		swiper.slideTo(index, reducedMotion ? 0 : 380);
	}

	return (
		<section
			className={styles.spotlight}
			data-testid="spotlight"
			data-active-index={active}
			data-source={source}
			data-autoplay={autoplay ? "playing" : "paused"}
			aria-label={source === "popular" ? "人気記事スライド" : "新着記事スライド"}
			aria-roledescription="カルーセル"
			onFocusCapture={(event) => {
				// キーボード由来のフォーカスだけを明示停止にする。
				if (event.target.matches(":focus-visible")) setManuallyPaused(true);
			}}
			onKeyDownCapture={(event) => {
				// ポインター選択後にキーボードへ切り替えた場合も停止する。再開ボタン自身は操作を妨げない。
				if (event.target instanceof HTMLElement && event.target.dataset.rotationControl !== "true")
					setManuallyPaused(true);
			}}
		>
			<div className={styles.viewer}>
				<Swiper
					id={carouselId}
					className={styles.swiper}
					slidesPerView={1}
					rewind
					speed={reducedMotion ? 0 : 380}
					onSwiper={setSwiper}
					onSlideChange={(instance) => setActive(instance.activeIndex)}
				>
					{articles.map((article, index) => (
						<SwiperSlide
							key={article.id}
							data-testid="spotlight-slide"
							data-article-id={article.id}
							inert={index !== active}
							aria-hidden={index !== active}
						>
							<article className={styles.card}>
								<ArticleLink
									articleId={article.id}
									href={article.url}
									placement="headline"
									className={styles.articleLink}
									tabIndex={index === active ? 0 : -1}
								>
									<SpotlightImage
										source={article.image}
										fallback={article.fallbackImage}
										first={index === 0}
									/>
									<span className={styles.shade} aria-hidden="true" />
									<span className={styles.badge}>
										{source === "popular" ? String(index + 1).padStart(2, "0") : "新着"}
									</span>
									<div className={styles.body}>
										<h2 data-testid="lead-story-title">{article.title}</h2>
										<div className={styles.meta}>
											<span>{article.host}</span>
											<time dateTime={article.publishedAt}>{article.publishedLabel}</time>
										</div>
									</div>
								</ArticleLink>
								{index === active && (
									<div className={styles.save}>
										<SaveArticle article={article} />
									</div>
								)}
							</article>
						</SwiperSlide>
					))}
				</Swiper>
				{multiple && (
					<div className={styles.controls}>
						<button
							type="button"
							className={styles.control}
							aria-label="前の記事を表示"
							aria-controls={carouselId}
							onClick={() => selectSlide((active - 1 + articles.length) % articles.length)}
						>
							‹
						</button>
						<div className={styles.selectors} aria-label="表示する記事">
							{articles.map((article, index) => (
								<button
									type="button"
									key={article.id}
									aria-label={index + 1 + "件目を表示: " + article.title}
									aria-current={index === active ? "true" : undefined}
									aria-controls={carouselId}
									onClick={() => selectSlide(index)}
								>
									{source === "popular" ? index + 1 : <span className={styles.dot} />}
								</button>
							))}
						</div>
						<span className={styles.position} aria-hidden="true">
							{active + 1} / {articles.length}
						</span>
						<button
							type="button"
							className={styles.control}
							aria-label="次の記事を表示"
							aria-controls={carouselId}
							onClick={() => selectSlide((active + 1) % articles.length)}
						>
							›
						</button>
						<button
							type="button"
							className={styles.pause}
							data-rotation-control="true"
							aria-label={manuallyPaused ? "自動切替を再開" : "自動切替を一時停止"}
							aria-pressed={manuallyPaused || reducedMotion}
							disabled={reducedMotion}
							onClick={() => setManuallyPaused((value) => !value)}
						>
							{manuallyPaused || reducedMotion ? "▶" : "Ⅱ"}
						</button>
					</div>
				)}
			</div>
			<ul
				className={styles.ranking}
				aria-label={source === "popular" ? "人気記事の順位" : "新着記事"}
			>
				{articles.map((article, index) => (
					<li key={article.id} data-active={index === active}>
						<ArticleLink
							articleId={article.id}
							href={article.url}
							placement="headline"
							className={styles.story}
							aria-label={article.title + "を掲載元で開く"}
						>
							<span className={styles.number} aria-hidden="true">
								{source === "popular" ? index + 1 : <span className={styles.dot} />}
							</span>
							<span className={styles.storyBody}>
								<strong>{article.title}</strong>
								<span>{article.host}</span>
							</span>
							<span className={styles.openArticle} aria-hidden="true">↗</span>
						</ArticleLink>
					</li>
				))}
			</ul>
		</section>
	);
}
